import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";
import { getAssetsPath } from "../../../../utils/pathConfig";

/**
 * PopiLoaderController - Handles preloader spine animation
 */
class LoaderController {
  constructor(
    container,
    dimensions,
    symbolWidth,
    symbolHeight,
    dimensionsContext
  ) {
    this.container = container;
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dimensionsContext = dimensionsContext;

    // Logo sprite
    this.logoSprite = null;

    // Spine instance
    this.preloaderSpine = null;
    this.isPlaying = false;
    this.initialized = false;

    // Load logo immediately, then initialize spine (both will display together)
    this.initializeLogoAndText();
    this.initializePreloaderSpine();
  }

  /**
   * Loads logo texture and creates logo sprite immediately.
   * Loads directly (not waiting for asset system) since this is the preloader.
   */
  async initializeLogoAndText() {
    try {
      const BASE_PATH = getAssetsPath();

      const logoPath = `${BASE_PATH}/images/preloader.webp`;

      // Load logo if not already cached
      if (!PIXI.Assets.cache.has(logoPath)) {
        await PIXI.Assets.load(logoPath);
      }

      const logoTexture = PIXI.Assets.get(logoPath);
      if (!logoTexture) {
        return;
      }

      // Create logo sprite
      this.logoSprite = new PIXI.Sprite(logoTexture);
      this.logoSprite.anchor.set(0.5);
      this.logoSprite.zIndex = 2; // Above spine (spine is zIndex 1)
      this.logoSprite.name = "popi_loader_logo";

      // Add to container
      this.container.addChild(this.logoSprite);

      // Apply initial scale and position
      this.applyLogoScaleAndPosition();
    } catch (error) {
      // Silent fail - loader will continue without logo
    }
  }

  /**
   * Applies responsive scaling and positioning to logo.
   */
  applyLogoScaleAndPosition() {
    if (!this.logoSprite || !this.dimensions || !this.logoSprite.parent) {
      return;
    }

    try {
      // Get device-specific multipliers and offset
      const deviceConfig = this.getLogoDeviceConfig();

      // Calculate logo scale based on symbol height
      const nativeSize = Settings.GRID_SYMBOL_HEIGHT;
      const targetSize = this.symbolHeight || nativeSize;
      const symbolScale = targetSize / nativeSize;
      const logoScale = deviceConfig.deviceMultiplier * symbolScale;

      // Apply logo scale and position (always dead center)
      this.logoSprite.scale.set(logoScale);
      this.logoSprite.x = this.dimensions.width / 2;
      this.logoSprite.y = this.dimensions.height / 2;
    } catch (error) {
      // Silent fail - logo will maintain previous scale/position
    }
  }

  /**
   * Returns device and orientation-specific multiplier for logo.
   * Provides detailed configuration for each device/orientation combination.
   */
  getLogoDeviceConfig() {
    if (!this.dimensionsContext) {
      return { deviceMultiplier: 1.0 };
    }

    const { device, orientation } = this.dimensionsContext;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return { deviceMultiplier: 1.0 }; // Mobile Portrait
      } else {
        return { deviceMultiplier: 0.6 }; // Mobile Landscape
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return { deviceMultiplier: 0.9 }; // Tablet Portrait
      } else {
        return { deviceMultiplier: 0.85 }; // Tablet Landscape
      }
    } else {
      return { deviceMultiplier: 0.5 }; // Desktop (no orientation)
    }
  }

  /**
   * Loads and initializes the preloader spine animation.
   * Loads assets directly (not from cache) since this is the preloader that runs before other assets load.
   */
  async initializePreloaderSpine() {
    try {
      const BASE_PATH = getAssetsPath();
      const jsonPath = `${BASE_PATH}/spines/preloader/preloader.json`;
      const atlasPath = `${BASE_PATH}/spines/preloader/preloader.atlas`;
      const skeletonAlias = "preloader-skeleton";
      const atlasAlias = "preloader-atlas";

      // Load assets if not already cached (handles re-initialization)
      if (!PIXI.Assets.cache.has(skeletonAlias)) {
        PIXI.Assets.add({ alias: skeletonAlias, src: jsonPath });
        PIXI.Assets.add({ alias: atlasAlias, src: atlasPath });
        await PIXI.Assets.load([skeletonAlias, atlasAlias]);
      }

      // Create spine instance from loaded assets
      this.preloaderSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!this.preloaderSpine?.skeleton?.data?.animations) {
        return;
      }

      // Add to container and initialize
      this.container.addChild(this.preloaderSpine);
      this.preloaderSpine.alpha = 1;
      this.preloaderSpine.zIndex = 10; // Increased to be above logo (logo is zIndex 2)
      this.initialized = true;

      this.applyScaleAndCenter();
      this.playAnimation();
    } catch (error) {
      // Silent fail - preloader will continue without animation
    }
  }

  /**
   * Extracts native dimensions from the spine skeleton data.
   * Used for proportional scaling calculations.
   */
  getNativeDimensions() {
    if (!this.preloaderSpine?.skeleton?.data) {
      return { width: 0, height: 0, x: 0, y: 0 };
    }

    const skeletonData = this.preloaderSpine.skeleton.data;
    return {
      width: skeletonData.width || 0,
      height: skeletonData.height || 0,
      x: skeletonData.x || 0,
      y: skeletonData.y || 0,
    };
  }

  /**
   * Returns device and orientation-specific multipliers for scale and Y position.
   * Y position is calculated as dimensions.height / yDivisor.
   */
  getDeviceMultipliers() {
    if (!this.dimensionsContext) {
      return { scale: 1.0, yDivisor: 2.5 };
    }

    const { device, orientation } = this.dimensionsContext;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return { scale: 1.15, yDivisor: 2.25 };
      } else {
        return { scale: 1.1, yDivisor: 2.85 };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return { scale: 1.0, yDivisor: 2.25 };
      } else {
        return { scale: 1.0, yDivisor: 2.5 };
      }
    } else {
      return { scale: 1.0, yDivisor: 3.5 };
    }
  }

  /**
   * Returns device and orientation-specific size multiplier and y offset for spine.
   * Provides detailed configuration for each device/orientation combination.
   * sizeMultiplier: scales the spine size (1.0 = normal size)
   * yOffset: multiplier relative to GRID_SYMBOL_HEIGHT (positive = down, negative = up).
   * Base position is bottom minus 1 grid symbol height, then yOffset is applied.
   */
  getSpineDeviceConfig() {
    if (!this.dimensionsContext) {
      return { sizeMultiplier: 1.0, yOffset: 0 };
    }

    const { device, orientation } = this.dimensionsContext;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return { sizeMultiplier: 1.0, yOffset: -0.5 }; // Mobile Portrait
      } else {
        return { sizeMultiplier: 1.0, yOffset: 0 }; // Mobile Landscape
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return { sizeMultiplier: 1.0, yOffset: -0.5 }; // Tablet Portrait
      } else {
        return { sizeMultiplier: 1.0, yOffset: -0.5 }; // Tablet Landscape
      }
    } else {
      return { sizeMultiplier: 1.0, yOffset: -1.5 }; // Desktop (no orientation)
    }
  }

  /**
   * Applies proportional scaling based on UI symbol height and device-specific multipliers.
   * Positions spine horizontally centered and at bottom minus 1 grid symbol height.
   */
  applyScaleAndCenter() {
    if (
      !this.preloaderSpine ||
      !this.dimensions ||
      !this.initialized ||
      !this.preloaderSpine.scale ||
      !this.preloaderSpine.parent
    ) {
      return;
    }

    try {
      // Calculate scale based on UI symbol height (GRID_SYMBOL_HEIGHT as reference)
      const nativeSize = Settings.GRID_SYMBOL_HEIGHT;
      const targetSize = this.symbolHeight || nativeSize;
      let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

      // Apply device/orientation-specific size multiplier
      const spineConfig = this.getSpineDeviceConfig();
      scale *= spineConfig.sizeMultiplier;

      this.preloaderSpine.scale.set(scale);

      // Position spine centered horizontally
      // Base position: bottom of screen minus 1 grid symbol height
      // Then apply yOffset (positive = down, negative = up)
      this.preloaderSpine.x = this.dimensions.width / 2;
      const baseY = this.dimensions.height - Settings.GRID_SYMBOL_HEIGHT;
      const yOffsetPixels = Settings.GRID_SYMBOL_HEIGHT * spineConfig.yOffset;
      this.preloaderSpine.y = baseY + yOffsetPixels;
    } catch (error) {
      // Silent fail - spine will maintain previous scale/position
    }
  }

  /**
   * Starts playing the preloader animation loop.
   */
  playAnimation() {
    if (!this.preloaderSpine?.state || this.isPlaying) return;

    try {
      this.isPlaying = true;
      this.preloaderSpine.state.clearTracks();
      this.preloaderSpine.state.clearListeners();
      this.preloaderSpine.skeleton.setToSetupPose();

      const availableAnimations = this.getAvailableAnimations();
      if (availableAnimations.length > 0) {
        this.preloaderSpine.state.setAnimation(0, availableAnimations[0], true);
      }
    } catch (error) {
      this.isPlaying = false;
    }
  }

  /**
   * Returns array of available animation names from the spine.
   */
  getAvailableAnimations() {
    return (
      this.preloaderSpine?.skeleton?.data?.animations.map((a) => a.name) || []
    );
  }

  /**
   * Updates dimensions and recalculates scale/position when screen size, symbol size, or device/orientation changes.
   */
  updateDimensions(dimensions, symbolWidth, symbolHeight, dimensionsContext) {
    const dimensionsChanged =
      !this.dimensions ||
      this.dimensions.width !== dimensions?.width ||
      this.dimensions.height !== dimensions?.height;
    const symbolWidthChanged = this.symbolWidth !== symbolWidth;
    const symbolHeightChanged = this.symbolHeight !== symbolHeight;
    const deviceChanged =
      !this.dimensionsContext ||
      this.dimensionsContext.device !== dimensionsContext?.device ||
      this.dimensionsContext.orientation !== dimensionsContext?.orientation;

    if (
      !dimensionsChanged &&
      !symbolWidthChanged &&
      !symbolHeightChanged &&
      !deviceChanged
    ) {
      return;
    }

    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    if (dimensionsContext) {
      this.dimensionsContext = dimensionsContext;
    }

    // Update logo positioning
    this.applyLogoScaleAndPosition();

    // Update spine if initialized
    if (this.initialized && this.preloaderSpine) {
      this.applyScaleAndCenter();
    }
  }

  /**
   * Stops animation and hides the spine.
   */
  stopAnimation() {
    if (!this.preloaderSpine || !this.initialized) return;

    this.isPlaying = false;
    this.preloaderSpine.alpha = 0.001;
    if (this.preloaderSpine.state) {
      this.preloaderSpine.state.clearTracks();
      this.preloaderSpine.state.clearListeners();
    }
  }

  /**
   * Cleans up all resources: removes logo, stops animation, removes spine from container, and destroys it.
   */
  cleanup() {
    this.isPlaying = false;

    // Clean up logo sprite
    if (this.logoSprite) {
      try {
        if (this.container?.children.includes(this.logoSprite)) {
          this.container.removeChild(this.logoSprite);
        }
        this.logoSprite.destroy();
        this.logoSprite = null;
      } catch (error) {
        this.logoSprite = null;
      }
    }

    // Clean up spine
    if (this.preloaderSpine) {
      try {
        if (this.preloaderSpine.state) {
          this.preloaderSpine.state.clearListeners();
          this.preloaderSpine.state.clearTracks();
          this.preloaderSpine.state.timeScale = 1.0;
        }

        if (this.container?.children.includes(this.preloaderSpine)) {
          this.container.removeChild(this.preloaderSpine);
        }

        this.preloaderSpine.destroy();
        this.preloaderSpine = null;
      } catch (error) {
        this.preloaderSpine = null;
      }
    }

    this.initialized = false;
    this.isPlaying = false;
  }
}

export default LoaderController;
